import { getCurrentLocale, translateCurrent, translatePluralCurrent } from '../i18n';

// `number` here means epoch milliseconds — the same raw representation
// ChatItem.lastActivityAtMs already uses for sorting (see its own doc
// comment: "Raw canonical activity time used for ordering; never derived
// from display text"). Accepting it directly here lets presentation code
// use that same canonical value instead of re-parsing an already-formatted
// display string (the root cause of the chat-list "9131d" bug — see
// ChatScreen.tsx's chatTime usage).
type TimestampLike = string | number | { toDate?: () => Date; seconds?: number } | null | undefined;

function toDate(value: TimestampLike): Date | null {
  if (!value) return null;
  let date: Date | null = null;
  if (typeof value === 'number') date = new Date(value);
  else if (typeof value === 'string') date = new Date(value);
  else if (typeof value.toDate === 'function') date = value.toDate();
  else if (typeof value.seconds === 'number') date = new Date(value.seconds * 1000);
  return date && !Number.isNaN(date.getTime()) ? date : null;
}

export function formatRelativeTime(value: TimestampLike): string {
  const date = toDate(value);
  if (!date) return '';
  const diff = (Date.now() - date.getTime()) / 1000;
  if (diff < 60) return translateCurrent('chat.now');
  if (diff < 3600) return translateCurrent('chat.minutesShort').replace('{count}', String(Math.floor(diff / 60)));
  if (diff < 86400) return translateCurrent('chat.hoursShort').replace('{count}', String(Math.floor(diff / 3600)));
  return translateCurrent('chat.daysShort').replace('{count}', String(Math.floor(diff / 86400)));
}

export function formatMemberCount(count: number): string {
  return translatePluralCurrent('chat.vibingCount', count);
}

/** "Last seen 5 min ago" / "Last seen 3h ago" / "Last seen yesterday" style copy. */
export function formatLastSeen(value: TimestampLike): string {
  const date = toDate(value);
  if (!date) return '';
  const diffMinutes = Math.floor((Date.now() - date.getTime()) / 60000);
  if (diffMinutes < 1) return translateCurrent('chat.lastSeenJustNow');
  if (diffMinutes < 60) return translatePluralCurrent('chat.lastSeenMinutes', diffMinutes);
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return translatePluralCurrent('chat.lastSeenHours', diffHours);
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return translateCurrent('chat.lastSeenYesterday');
  if (diffDays < 7) return translatePluralCurrent('chat.lastSeenDays', diffDays);
  return translateCurrent('chat.lastSeenAWhileAgo');
}

/** "Seen" / "Seen 2 min ago" / "Delivered" for outgoing message receipts. */
export function formatSeenReceipt(value: TimestampLike): string {
  const date = toDate(value);
  if (!date) return translateCurrent('chat.seen');
  const diffMinutes = Math.floor((Date.now() - date.getTime()) / 60000);
  if (diffMinutes < 1) return translateCurrent('chat.seenJustNow');
  if (diffMinutes < 60) return translatePluralCurrent('chat.seenMinutes', diffMinutes);
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return translatePluralCurrent('chat.seenHours', diffHours);
  return translateCurrent('chat.seen');
}

/** Date-separator pill label: "Today" / "Yesterday" / "Monday" / "Jan 4". */
export function formatDateSeparatorLabel(value: TimestampLike): string {
  const date = toDate(value);
  if (!date) return '';
  const now = new Date();
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((startOfDay(now) - startOfDay(date)) / 86400000);
  if (diffDays === 0) return translateCurrent('feed.today');
  if (diffDays === 1) return translateCurrent('chat.yesterday');
  const locale = getCurrentLocale();
  if (diffDays < 7) return date.toLocaleDateString(locale, { weekday: 'long' });
  return date.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
}

export function toEpochMs(value: TimestampLike): number {
  return toDate(value)?.getTime() ?? 0;
}

/** True if two messages are close enough in time + same sender to visually group. */
export function shouldGroupWithPrevious(
  currentSenderId: string,
  currentCreatedAt: TimestampLike,
  previousSenderId: string | undefined,
  previousCreatedAt: TimestampLike | undefined,
): boolean {
  if (!previousSenderId || currentSenderId !== previousSenderId) return false;
  const gapMs = toEpochMs(currentCreatedAt) - toEpochMs(previousCreatedAt);
  return gapMs >= 0 && gapMs < 60_000;
}

export function isSameCalendarDay(a: TimestampLike, b: TimestampLike | undefined): boolean {
  const dateA = toDate(a);
  const dateB = toDate(b);
  if (!dateA || !dateB) return false;
  return dateA.getFullYear() === dateB.getFullYear()
    && dateA.getMonth() === dateB.getMonth()
    && dateA.getDate() === dateB.getDate();
}
